import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { CONSENT_TEXT } from "@/data/seo";

const KEY = "ap_consent";
const DEFAULT = { necessary: true, media: false };

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : null;
  } catch (_) {
    return null;
  }
};

const ConsentContext = createContext(null);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(read);
  const [open, setOpen] = useState(false);
  const save = useCallback((next) => {
    const value = { ...DEFAULT, ...next, necessary: true, ts: new Date().toISOString() };
    try { localStorage.setItem(KEY, JSON.stringify(value)); } catch (_) {}
    setConsent(value);
    setOpen(false);
  }, []);
  const value = useMemo(() => ({
    consent,
    hasDecided: !!consent,
    media: !!consent?.media,
    acceptAll: () => save({ media: true }),
    rejectAll: () => save({ media: false }),
    save,
    openSettings: () => setOpen(true),
    closeSettings: () => setOpen(false),
    settingsOpen: open,
  }), [consent, open, save]);
  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);

const Toggle = ({ checked, onChange, disabled, label, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors ${checked ? "border-[#C5A059] bg-[#C5A059]" : "border-white/25 bg-transparent"} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
  >
    <span className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-[#0B0B0C] transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"} ${checked ? "" : "bg-[#A1A1AA]"}`} />
  </button>
);

const Panel = ({ children, testId }) => (
  <motion.div
    initial={{ y: 30, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    exit={{ y: 30, opacity: 0 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="consent-title"
    data-testid={testId}
    className="fixed bottom-4 left-4 right-4 z-[120] mx-auto max-w-2xl border border-white/10 bg-[#141416]/95 p-6 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] backdrop-blur sm:bottom-8 sm:p-8"
  >
    {children}
  </motion.div>
);

const btn = "px-6 py-3 font-sans text-[10px] font-medium uppercase tracking-[0.25em] transition-colors";

export const ConsentBanner = () => {
  const { t: tt, lang, lp } = useLang();
  const c = useConsent();
  const t = CONSENT_TEXT[lang];
  const [media, setMedia] = useState(c.media);
  useEffect(() => setMedia(c.media), [c.media, c.settingsOpen]);
  const showBanner = !c.hasDecided && !c.settingsOpen;
  useEffect(() => {
    if (!c.settingsOpen) return;
    const onKey = (e) => e.key === "Escape" && c.closeSettings();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [c]);

  return (
    <AnimatePresence>
      {showBanner && (
        <Panel key="banner" testId="consent-banner">
          <p id="consent-title" className="font-serif text-2xl font-light text-[#F9F8F6]">{t.title}</p>
          <p className="mt-3 font-sans text-xs leading-relaxed text-[#A1A1AA]">
            {t.intro}{" "}
            <Link to={lp("/datenschutz")} data-testid="consent-privacy-link" className="underline underline-offset-2 hover:text-[#C5A059]">{t.privacyLink}</Link>.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="consent-accept-all" onClick={c.acceptAll} className={`${btn} bg-[#C5A059] text-[#0B0B0C] hover:bg-[#E5C158]`}>{t.acceptAll}</button>
            <button data-testid="consent-reject-all" onClick={c.rejectAll} className={`${btn} border border-white/20 text-[#F9F8F6] hover:border-[#C5A059] hover:text-[#C5A059]`}>{t.rejectAll}</button>
            <button data-testid="consent-open-settings" onClick={c.openSettings} className={`${btn} text-[#A1A1AA] hover:text-[#C5A059]`}>{t.settings}</button>
          </div>
        </Panel>
      )}
      {c.settingsOpen && (
        <Panel key="settings" testId="consent-settings">
          <div className="flex items-start justify-between gap-6">
            <p id="consent-title" className="font-serif text-2xl font-light text-[#F9F8F6]">{t.title}</p>
            <button data-testid="consent-close" onClick={c.closeSettings} aria-label={t.close} className="font-mono text-[10px] tracking-[0.3em] text-[#71717A] hover:text-[#C5A059]">{t.close.toUpperCase()}</button>
          </div>
          <ul className="mt-6 space-y-5">
            <li className="flex items-start justify-between gap-6">
              <div>
                <p className="font-sans text-sm text-[#F9F8F6]">{t.categories.necessary.label} <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">{t.categories.necessary.always}</span></p>
                <p className="mt-1 font-sans text-xs leading-relaxed text-[#71717A]">{t.categories.necessary.desc}</p>
              </div>
              <Toggle checked disabled onChange={() => {}} label={t.categories.necessary.label} testId="consent-toggle-necessary" />
            </li>
            <li className="flex items-start justify-between gap-6">
              <div>
                <p className="font-sans text-sm text-[#F9F8F6]">{t.categories.media.label}</p>
                <p className="mt-1 font-sans text-xs leading-relaxed text-[#71717A]">{t.categories.media.desc}</p>
              </div>
              <Toggle checked={media} onChange={setMedia} label={t.categories.media.label} testId="consent-toggle-media" />
            </li>
          </ul>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="consent-save" onClick={() => c.save({ media })} className={`${btn} bg-[#C5A059] text-[#0B0B0C] hover:bg-[#E5C158]`}>{t.save}</button>
            <button data-testid="consent-settings-accept-all" onClick={c.acceptAll} className={`${btn} border border-white/20 text-[#F9F8F6] hover:border-[#C5A059] hover:text-[#C5A059]`}>{t.acceptAll}</button>
            <button data-testid="consent-settings-reject-all" onClick={c.rejectAll} className={`${btn} text-[#A1A1AA] hover:text-[#C5A059]`}>{t.rejectAll}</button>
          </div>
          <p className="mt-4 font-sans text-[11px] text-[#71717A]">
            <Link to={lp("/datenschutz")} className="underline underline-offset-2 hover:text-[#C5A059]">{tt.footer.privacy}</Link> · <Link to={lp("/impressum")} className="underline underline-offset-2 hover:text-[#C5A059]">{tt.footer.imprint}</Link>
          </p>
        </Panel>
      )}
    </AnimatePresence>
  );
};

/* Placeholder shown instead of a YouTube iframe until consent is given (once or persistently) */
export const MediaGate = ({ poster, title, children, className = "", compact = false }) => {
  const { lang } = useLang();
  const c = useConsent();
  const [once, setOnce] = useState(false);
  const t = CONSENT_TEXT[lang];
  if (c.media || once) return children;
  return (
    <div data-testid="media-gate" className={`relative flex h-full w-full items-center justify-center overflow-hidden bg-[#0B0B0C] ${className}`}>
      {poster && <img loading="lazy" decoding="async" src={poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
      <div className={`relative z-10 max-w-md text-center ${compact ? "p-4" : "p-8"}`}>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">{t.placeholderTitle}</p>
        {title && <p className="mt-2 font-serif text-xl font-light text-[#F9F8F6]">{title}</p>}
        {!compact && <p className="mt-3 font-sans text-xs leading-relaxed text-[#A1A1AA]">{t.placeholderText}</p>}
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <button data-testid="media-gate-load-once" onClick={() => setOnce(true)} className={`${btn} bg-[#C5A059] text-[#0B0B0C] hover:bg-[#E5C158]`}>{t.loadOnce}</button>
          <button data-testid="media-gate-load-always" onClick={() => c.save({ media: true })} className={`${btn} border border-white/20 text-[#F9F8F6] hover:border-[#C5A059] hover:text-[#C5A059]`}>{t.loadAlways}</button>
        </div>
      </div>
    </div>
  );
};
